import React, { useState, useEffect } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Button, Tabs, Form, Input, Switch, Select, message, Divider, Space, Avatar, Spin, Upload, Modal, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  UserOutlined, 
  LockOutlined, 
  ApiOutlined,
  SaveOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  WalletOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { authHeader } from '../../../utils/api';
import axios from 'axios';
import Integrations from '../Integrations/Integrations';
import BillingPlans from '../Billing/BillingPlans';
import CRMAuth from './CRMAuth';

const { Title, Paragraph, Text } = Typography;

const Settings = () => {
  const { currentUser, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [geotabForm] = Form.useForm();
  const [smtpForm] = Form.useForm();
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpProvider, setSmtpProvider] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [supportModalVisible, setSupportModalVisible] = useState(false);

  // Check if user role is restricted (client, agency, enterprise)
  const userRole = currentUser?.role?.toLowerCase();
  const isRestricted = ['client', 'agency', 'enterprise'].includes(userRole);

  // Helper to get the correct avatar URL
  const getAvatarUrl = (url) => {
    if (!url) return null;
    
    // If it's already an absolute URL (S3 or other), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If it's a relative URL, prepend the API base URL
    const getApiBaseUrl = () => {
      const explicit = process.env.REACT_APP_API_URL;
      if (explicit) return explicit.replace(/\/api$/, '');
      
      const host = window.location.hostname?.toLowerCase();
      const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
      if (isLocal) return 'http://localhost:5000';
      
      return 'https://api.pathmarkr.com';
    };
    
    return `${getApiBaseUrl()}${url}`;
  };

  // Helper function to convert file to data URL
  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Handle avatar upload
  const handleAvatarUpload = async ({ file, onSuccess, onError }) => {
    try {
      if (!file || !(file).type?.startsWith('image/')) {
        message.error('Please select a valid image file');
        return onError?.(new Error('Invalid file'));
      }
      setUploading(true);
      const dataUrl = await fileToDataUrl(file);
      
      const response = await axios.post(`/api/users/${currentUser.id}/profile-picture`, {
        dataUrl
      }, {
        headers: authHeader()
      });
      
      if (response.data) {
        updateUser({ profile_picture_url: response.data.profile_picture_url });
        message.success('Profile picture updated successfully');
        onSuccess?.(response.data);
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      message.error(error.response?.data?.error || 'Failed to upload profile picture');
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  // Load user profile data
  useEffect(() => {
    const loadProfileData = async () => {
      if (!currentUser?.id) {
        console.log('❌ No currentUser.id, skipping profile data load');
        return;
      }
      
      console.log('🔄 Starting profile data fetch for user ID:', currentUser.id);
      
      try {
        setDataLoading(true);
        console.log('📡 Making API call to:', `/api/users/${currentUser.id}`);
        console.log('🔑 Auth headers:', authHeader());
        
        const response = await axios.get(`/api/users/${currentUser.id}`, { 
          headers: authHeader() 
        });
        
        console.log('✅ API Response received:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data
        });
        
        if (response.data) {
          const userData = response.data;
          
          console.log('📋 Profile data loaded:', {
            full_name: userData.full_name,
            email: userData.email,
            phone: userData.phone,
            allFields: Object.keys(userData)
          });
          
          // Use fallback from currentUser if API doesn't have full_name
          const fullName = userData.full_name || currentUser?.full_name || currentUser?.name || '';
          console.log('🏷️ Final fullName value:', fullName);
          console.log('🏷️ currentUser data:', {
            full_name: currentUser?.full_name,
            name: currentUser?.name,
            first_name: currentUser?.first_name,
            last_name: currentUser?.last_name
          });
          
          profileForm.setFieldsValue({
            fullName: fullName,
            email: userData.email || currentUser?.email || '',
            phone: userData.phone || currentUser?.phone || '',
            company: userData.company || currentUser?.company || '',
            jobTitle: userData.job_title || userData.jobTitle || currentUser?.job_title || '',
            timezone: userData.timezone || currentUser?.timezone || 'America/New_York',
            language: userData.language || currentUser?.language || 'en',
            emailNotifications: userData.email_notifications ?? currentUser?.email_notifications ?? true,
            marketingEmails: userData.marketing_emails ?? currentUser?.marketing_emails ?? false,
          });
          
          console.log('✅ Form fields populated successfully');
          
          // Debug: Check if form field was actually set
          setTimeout(() => {
            const formValues = profileForm.getFieldsValue();
            console.log('🔍 Form values after setting:', formValues);
            console.log('🔍 Specifically fullName field:', formValues.fullName);
          }, 100);
        } else {
          console.log('⚠️ No data in API response');
        }
      } catch (error) {
        console.error('❌ Failed to load profile data:', {
          error: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: `/api/users/${currentUser.id}`
        });
        message.error('Failed to load profile data: ' + (error.response?.data?.message || error.message));
      } finally {
        setDataLoading(false);
        console.log('🏁 Profile data loading finished');
      }
    };

    loadProfileData();
  }, [currentUser, profileForm]);

  // Load SMTP settings (only when the smtp tab is active)
  useEffect(() => {
    const loadSmtpSettings = async () => {
      if (!currentUser?.id || activeTab !== 'smtp') return;
      try {
        const response = await axios.get('/api/clients/me/smtp-settings', { headers: authHeader() });
        const s = response.data;
        setSmtpConfigured(s.configured || false);
        smtpForm.setFieldsValue({
          host: s.host || '',
          port: s.port || 587,
          user: s.user || '',
          pass: '',
          secure: s.secure || false,
          from_name: s.from_name || '',
        });
      } catch (error) {
        if (error.response?.status === 401) { logout(); navigate('/login'); }
      }
    };
    loadSmtpSettings();
  }, [currentUser, activeTab, smtpForm, logout, navigate]);

  // Load GeoTab settings (only when the geotab tab is active)
  useEffect(() => {
    const loadGeotabSettings = async () => {
      if (!currentUser?.id || activeTab !== 'geotab') return;
      
      try {
        console.log('Loading GeoTab settings from /api/clients/me/geotab-settings');
        const response = await axios.get('/api/clients/me/geotab-settings', { 
          headers: authHeader() 
        });
        
        console.log('GeoTab settings response:', response.data);
        
        if (response.data) {
          const settings = response.data;
          geotabForm.setFieldsValue({
            database: settings.database || '',
            username: settings.username || '',
            password: '', // Don't populate password for security
            server: settings.server || 'my.geotab.com',
            enabled: settings.enabled ?? false,
          });
        }
      } catch (error) {
        console.error('Failed to load GeoTab settings:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url
        });
        
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          message.error('Session expired. Please log in again.');
          logout();
          navigate('/login');
          return;
        }
        
        // Handle 404 Not Found
        if (error.response?.status === 404) {
          console.warn('GeoTab settings endpoint not found. Using default values.');
          // Set default values for new users
          geotabForm.setFieldsValue({
            database: '',
            username: '',
            password: '',
            server: 'my.geotab.com',
            enabled: false,
          });
          return;
        }
        
        // Don't show error message for other errors as this might be expected for new users
      }
    };

    loadGeotabSettings();
  }, [currentUser, activeTab, geotabForm, logout, navigate]);

  const handleProfileSave = async (values) => {
    try {
      setLoading(true);
      const response = await axios.put(`/api/users/${currentUser.id}`, {
        full_name: values.fullName,
        email: values.email,
        phone: values.phone,
        company: values.company,
        job_title: values.jobTitle,
        timezone: values.timezone,
        language: values.language,
        email_notifications: values.emailNotifications,
        marketing_emails: values.marketingEmails
      }, { headers: authHeader() });

      if (response.data) {
        updateUser(response.data);
        message.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      message.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySave = async (values) => {
    try {
      setLoading(true);
      await axios.put(`/api/users/${currentUser.id}/password`, {
        current_password: values.currentPassword,
        new_password: values.newPassword
      }, { headers: authHeader() });

      message.success('Password updated successfully');
      securityForm.resetFields();
    } catch (error) {
      console.error('Security update error:', error);
      message.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const SMTP_PROVIDERS = [
    { value: 'gmail',      label: '📧 Gmail',                   host: 'smtp.gmail.com',               port: 587, secure: false,
      tip: <span>Gmail requires an <strong>App Password</strong> (not your regular password). <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">Create one here</a> after enabling 2-Step Verification.</span> },
    { value: 'outlook',   label: '📧 Outlook / Microsoft 365', host: 'smtp.office365.com',           port: 587, secure: false,
      tip: <span>Use your full Outlook/Microsoft 365 email and password. If MFA is on, create an <strong>App Password</strong> in your Microsoft account security settings.</span> },
    { value: 'hotmail',   label: '📧 Hotmail / Live',          host: 'smtp.live.com',                port: 587, secure: false,
      tip: <span>Use your full Hotmail/Live email and password. If 2FA is on, create an <strong>App Password</strong> in your Microsoft account.</span> },
    { value: 'yahoo',     label: '📧 Yahoo Mail',              host: 'smtp.mail.yahoo.com',          port: 587, secure: false,
      tip: <span>Yahoo requires an <strong>App Password</strong>. Go to <a href="https://login.yahoo.com/account/security" target="_blank" rel="noreferrer">Yahoo Account Security</a>, enable 2FA, then generate an App Password.</span> },
    { value: 'godaddy',   label: '🏢 GoDaddy Email',           host: 'smtpout.secureserver.net',     port: 465, secure: true,
      tip: <span>Use your GoDaddy Workspace email address and its password. Port 465 with SSL is recommended.</span> },
    { value: 'hostinger', label: '🏢 Hostinger Email',         host: 'smtp.hostinger.com',           port: 465, secure: true,
      tip: <span>Use your Hostinger email address and the password you set for that mailbox in hPanel.</span> },
    { value: 'zoho',      label: '🏢 Zoho Mail',               host: 'smtp.zoho.com',                port: 587, secure: false,
      tip: <span>Use your Zoho email and password. If 2FA is on, generate an <strong>App Password</strong> in Zoho account settings.</span> },
    { value: 'sendgrid',  label: '🚀 SendGrid',                host: 'smtp.sendgrid.net',            port: 587, secure: false,
      tip: <span>Username must be the literal word <code>apikey</code>. Password is your <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noreferrer">SendGrid API Key</a>.</span> },
    { value: 'mailgun',   label: '🚀 Mailgun',                 host: 'smtp.mailgun.org',             port: 587, secure: false,
      tip: <span>Use your Mailgun SMTP login and password found in Mailgun dashboard → Sending → Domain Settings → SMTP credentials.</span> },
    { value: 'custom',    label: '⚙️ Custom / Other',          host: '',                             port: 587, secure: false,
      tip: <span>Enter your SMTP server details manually. Check your email provider's documentation.</span> },
  ];

  const handleProviderChange = (value) => {
    setSmtpProvider(value);
    const preset = SMTP_PROVIDERS.find(p => p.value === value);
    if (preset) {
      smtpForm.setFieldsValue({ host: preset.host, port: preset.port, secure: preset.secure });
    }
  };

  const handleSmtpSave = async (values) => {
    try {
      setLoading(true);
      await axios.put('/api/clients/me/smtp-settings', {
        host: values.host,
        port: values.port,
        user: values.user,
        pass: values.pass,
        secure: values.secure,
        from_name: values.from_name,
      }, { headers: authHeader() });
      message.success('Email settings saved successfully');
      setSmtpConfigured(!!(values.host && values.user && values.pass));
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to save email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpTest = async () => {
    const values = smtpForm.getFieldsValue();
    if (!values.host || !values.user || !values.pass) {
      message.warning('Fill in Host, Email, and Password before testing');
      return;
    }
    try {
      setLoading(true);
      await axios.put('/api/clients/me/smtp-settings', {
        host: values.host, port: values.port, user: values.user,
        pass: values.pass, secure: values.secure, from_name: values.from_name,
      }, { headers: authHeader() });
      const res = await axios.post('/api/email-campaigns/test-smtp', {}, { headers: authHeader() });
      message.success(res.data?.message || 'SMTP connection successful ✅');
    } catch (error) {
      message.error(error.response?.data?.error || 'SMTP connection failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGeotabSave = async (values) => {
    try {
      setLoading(true);
      console.log('Saving GeoTab settings to /api/clients/me/geotab-settings');
      
      const response = await axios.put('/api/clients/me/geotab-settings', {
        database: values.database,
        username: values.username,
        password: values.password,
        server: values.server || 'my.geotab.com',
        enabled: values.enabled
      }, { headers: authHeader() });
      
      console.log('GeoTab settings saved:', response.data);
      message.success('GeoTab settings saved successfully');
    } catch (error) {
      console.error('GeoTab settings error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      
      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        message.error('Session expired. Please log in again.');
        logout();
        navigate('/login');
        return;
      }
      
      // Handle 404 Not Found
      if (error.response?.status === 404) {
        message.error('GeoTab settings endpoint not found. Please contact support.');
        return;
      }
      
      message.error(error.response?.data?.error || error.response?.data?.message || 'Failed to save GeoTab settings');
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined /> Profile Settings
        </span>
      ),
      children: (
        <div>
          <Card>
            <Spin spinning={dataLoading}>
              <Row gutter={24}>
                <Col span={24} style={{ marginBottom: 24 }}>
                  <Space direction="vertical" align="center" style={{ width: '100%' }}>
                    <Avatar 
                      size={100} 
                      src={getAvatarUrl(currentUser?.profile_picture_url)} 
                      icon={<UserOutlined />} 
                    />
                    <Upload
                      name="file"
                      accept="image/*"
                      showUploadList={false}
                      maxCount={1}
                      customRequest={handleAvatarUpload}
                    >
                      <Button icon={<UploadOutlined />} loading={uploading || dataLoading}>
                        Change Avatar
                      </Button>
                    </Upload>
                  </Space>
                </Col>
              </Row>

              <Form
                form={profileForm}
                layout="vertical"
                onFinish={handleProfileSave}
              >
              <Title level={5}>Personal Information</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Full Name" name="fullName" rules={[{ required: true }]}>
                    <Input size="large" prefix={<UserOutlined />} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                    <Input size="large" prefix={<MailOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Phone" name="phone">
                    <Input size="large" prefix={<PhoneOutlined />} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Company Information</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Company Name" name="company">
                    <Input size="large" prefix={<EnvironmentOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Job Title" name="jobTitle">
                    <Input size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Preferences</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Timezone" name="timezone">
                    <Select size="large">
                      <Select.Option value="America/New_York">Eastern Time (ET)</Select.Option>
                      <Select.Option value="America/Chicago">Central Time (CT)</Select.Option>
                      <Select.Option value="America/Denver">Mountain Time (MT)</Select.Option>
                      <Select.Option value="America/Los_Angeles">Pacific Time (PT)</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Language" name="language">
                    <Select size="large" prefix={<GlobalOutlined />}>
                      <Select.Option value="en">English</Select.Option>
                      <Select.Option value="es">Spanish</Select.Option>
                      <Select.Option value="fr">French</Select.Option>
                      <Select.Option value="de">German</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Notifications</Title>
              <Form.Item label="Email Notifications" name="emailNotifications" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item label="Marketing Emails" name="marketingEmails" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item style={{ marginTop: 24 }}>
                <Button type="primary" htmlType="submit" size="large" icon={<SaveOutlined />} loading={loading}>
                  Save Changes
                </Button>
              </Form.Item>
            </Form>
            </Spin>
          </Card>
        </div>
      )
    },
    {
      key: 'security',
      label: (
        <span>
          <LockOutlined /> Security & Login
        </span>
      ),
      children: (
        <div>
          <Card title="Change Password" style={{ marginBottom: 24 }}>
            <Form
              form={securityForm}
              layout="vertical"
              onFinish={handleSecuritySave}
            >
              <Form.Item 
                label="Current Password" 
                name="currentPassword" 
                rules={[{ required: true, message: 'Please enter your current password' }]}
              >
                <Input.Password size="large" prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item 
                label="New Password" 
                name="newPassword" 
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 8, message: 'Password must be at least 8 characters' }
                ]}
              >
                <Input.Password size="large" prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item 
                label="Confirm New Password" 
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password size="large" prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" icon={<SaveOutlined />} loading={loading}>
                  Update Password
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card title="Two-Factor Authentication">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text strong>Status: </Text>
                <Text type="secondary">Disabled</Text>
              </div>
              <Paragraph type="secondary">
                Add an extra layer of security to your account by enabling two-factor authentication.
              </Paragraph>
              <Button type="primary">Enable 2FA</Button>
            </Space>
          </Card>

          <Card title="Active Sessions" style={{ marginTop: 24 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card type="inner" size="small">
                <Row align="middle" justify="space-between">
                  <Col>
                    <Space direction="vertical" size="small">
                      <Text strong>Current Session</Text>
                      <Text type="secondary">Windows • Chrome • 192.168.1.100</Text>
                      <Text type="secondary">Last active: Just now</Text>
                    </Space>
                  </Col>
                  <Col>
                    <Button disabled>Current</Button>
                  </Col>
                </Row>
              </Card>
            </Space>
          </Card>
        </div>
      )
    },
    {
      key: 'geotab',
      label: (
        <span>
          <ApiOutlined /> GeoTab Settings
        </span>
      ),
      children: (
        <div style={{ position: 'relative' }}>
          {/* Grey overlay for restricted users */}
          {isRestricted && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(3px)',
              borderRadius: '8px'
            }}>
              <Card
                style={{ maxWidth: 500, textAlign: 'center' }}
                actions={[
                  <Button
                    key="contact"
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={() => setSupportModalVisible(true)}
                    size="large"
                  >
                    Contact Support
                  </Button>
                ]}
              >
                <LockOutlined style={{ fontSize: 64, color: '#ff4d4f', marginBottom: 16 }} />
                <Title level={3}>Access Restricted</Title>
                <Paragraph>
                  Please contact support if you need access to GeoTab settings.
                </Paragraph>
              </Card>
            </div>
          )}

          <div style={{ filter: isRestricted ? 'blur(5px) grayscale(100%)' : 'none', pointerEvents: isRestricted ? 'none' : 'auto' }}>
          <Card>
            <Paragraph type="secondary">
              Configure your GeoTab integration settings to access GPS tracking and vehicle data.
            </Paragraph>

            <Form
              form={geotabForm}
              layout="vertical"
              onFinish={handleGeotabSave}
              initialValues={{
                database: '',
                username: '',
                password: '',
                server: 'my.geotab.com',
                enabled: false,
              }}
            >
              <Form.Item 
                label="Database Name" 
                name="database"
                rules={[{ required: true, message: 'Please enter your GeoTab database name' }]}
              >
                <Input size="large" placeholder="e.g., my_database" />
              </Form.Item>

              <Form.Item 
                label="Username" 
                name="username"
                rules={[{ required: true, message: 'Please enter your GeoTab username' }]}
              >
                <Input size="large" placeholder="GeoTab username" />
              </Form.Item>

              <Form.Item 
                label="Password" 
                name="password"
                rules={[{ required: true, message: 'Please enter your GeoTab password' }]}
              >
                <Input.Password size="large" placeholder="GeoTab password" />
              </Form.Item>

              <Form.Item 
                label="Server" 
                name="server"
              >
                <Input size="large" placeholder="my.geotab.com" />
              </Form.Item>

              <Form.Item label="Enable GeoTab Integration" name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Divider />

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" size="large" icon={<SaveOutlined />} loading={loading}>
                    Save Settings
                  </Button>
                  <Button size="large" loading={loading}>Test Connection</Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
          </div>
        </div>
      )
    },
    {
      key: 'smtp',
      label: (
        <span>
          <MailOutlined /> Email Settings
        </span>
      ),
      children: (
        <div>
          <Card>
            <Paragraph type="secondary">
              Configure your own SMTP server so all campaign emails are sent from <strong>your email address</strong> instead of the platform's shared address.
            </Paragraph>
            {smtpConfigured && (
              <div style={{ marginBottom: 16, padding: '8px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#15803d', fontSize: 13 }}>
                ✅ Your SMTP is configured — emails will be sent from your own email address.
              </div>
            )}
            {/* Provider selector */}
            <div style={{ marginBottom: 20 }}>
              <Text strong>Select your email provider</Text>
              <Select
                size="large"
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Choose your email provider..."
                value={smtpProvider || undefined}
                onChange={handleProviderChange}
                options={SMTP_PROVIDERS.map(p => ({ value: p.value, label: p.label }))}
              />
            </div>

            {/* Provider-specific instructions */}
            {smtpProvider && (
              <div style={{ marginBottom: 20, padding: '10px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, fontSize: 13 }}>
                💡 {SMTP_PROVIDERS.find(p => p.value === smtpProvider)?.tip}
              </div>
            )}

            <Form
              form={smtpForm}
              layout="vertical"
              onFinish={handleSmtpSave}
              initialValues={{ host: '', port: 587, user: '', pass: '', secure: false, from_name: '' }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Your Email Address" name="user" rules={[{ required: true, type: 'email', message: 'Enter your email address' }]}>
                    <Input size="large" placeholder="you@yourcompany.com" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Password / App Password" name="pass" rules={[{ required: !smtpConfigured, message: 'Enter your password' }]}>
                    <Input.Password size="large" placeholder={smtpConfigured ? 'Leave blank to keep current password' : 'Your email password or app password'} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Sender Display Name" name="from_name">
                    <Input size="large" placeholder="e.g. ABC HVAC Services" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ fontSize: 13, color: '#888' }}>Advanced (auto-filled by provider)</Divider>

              <Row gutter={16}>
                <Col xs={24} md={16}>
                  <Form.Item label="SMTP Host" name="host" rules={[{ required: true, message: 'Enter SMTP host' }]}>
                    <Input size="large" placeholder="e.g. smtp.gmail.com" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Port" name="port">
                    <Input size="large" type="number" placeholder="587" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Use SSL/TLS (port 465)" name="secure" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item style={{ marginTop: 16 }}>
                <Space>
                  <Button type="primary" htmlType="submit" size="large" icon={<SaveOutlined />} loading={loading}>
                    Save Settings
                  </Button>
                  <Button size="large" loading={loading} onClick={handleSmtpTest}>
                    Test Connection
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </div>
      )
    },
    {
      key: 'crm-auth',
      label: (
        <span>
          <ApiOutlined /> CRM Auth
        </span>
      ),
      children: <CRMAuth />
    },
    {
      key: 'integrations',
      label: (
        <span>
          <AppstoreOutlined /> Integrations
        </span>
      ),
      children: <Integrations />
    },
    {
      key: 'billing',
      label: (
        <span>
          <WalletOutlined /> Billing & Plans
        </span>
      ),
      children: <BillingPlans />
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Settings' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Settings</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Manage your account settings and preferences
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        type="card"
      />

      {/* Support Contact Modal */}
      <Modal
        title="Contact Support"
        open={supportModalVisible}
        onCancel={() => setSupportModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSupportModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        <Alert
          message="Need Access to GeoTab Settings?"
          description={
            <div>
              <Paragraph>
                If you need access to GeoTab settings or have any issues, please contact our support team:
              </Paragraph>
              <ul style={{ marginLeft: 20 }}>
                <li><strong>Email:</strong> support@pathmarkr.com</li>
                <li><strong>Phone:</strong> +1 (555) 123-4567</li>
              </ul>
              <Paragraph style={{ marginTop: 12 }}>
                Our support team will assist you with your request within 24 hours.
              </Paragraph>
            </div>
          }
          type="info"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default Settings;
